import { NavigateFunction } from "react-router-dom";
import { logout } from "../services/api/apiClient";
import { logoutParams } from "../services/api/types/auth/login";

export interface HandleLogoutParams {
  userEmail: string;
  authDispatch: (action: { type: "CLEAR_USER" }) => void;
  customisationsDispatch: (action: { type: "RESET_ALL_PAGES" }) => void;
  portalsDispatch: (
    action:
      | { type: "SET_PORTALS"; payload: [] }
      | { type: "CLEAR_SELECTED_PORTAL" },
  ) => void;
  runGuestBootstrap: () => Promise<void>;
  navigate: NavigateFunction;
  showToast?: (
    message: string,
    type: "error" | "success" | "info",
    duration?: number,
  ) => void;
  setLoading?: (loading: boolean) => void;
}

export const clearAuthSessionStorage = (): void => {
  localStorage.clear();
  sessionStorage.clear();
};

/**
 * Handles user logout by:
 * 1. Calling the logout API
 * 2. Clearing all localStorage items
 * 3. Dispatching clear data actions to contexts
 * 4. Navigating to login page
 *
 * @param params - Object containing userEmail, dispatch, navigate, and optional showToast/setLoading
 */
export const handleLogout = async ({
  userEmail,
  authDispatch,
  customisationsDispatch,
  portalsDispatch,
  runGuestBootstrap,
  navigate,
  showToast,
  setLoading,
}: HandleLogoutParams): Promise<void> => {
  try {
    if (setLoading) {
      setLoading(true);
    }

    const body: logoutParams = {
      username: userEmail,
    };
    await logout(body);

    clearAuthSessionStorage();

    authDispatch({ type: "CLEAR_USER" });
    customisationsDispatch({ type: "RESET_ALL_PAGES" });
    portalsDispatch({ type: "SET_PORTALS", payload: [] });
    portalsDispatch({ type: "CLEAR_SELECTED_PORTAL" });
    await runGuestBootstrap();
    navigate("/login");
  } catch (error: any) {
    console.log("Error:", error);
    if (showToast) {
      const errorMessage =
        error?.response?.data || "An error occurred during logout";
      showToast(errorMessage, "error");
    }
  } finally {
    if (setLoading) {
      setLoading(false);
    }
  }
};
